import { isSaturday, isSunday, nextMonday, startOfWeek, endOfWeek, sub, format } from 'date-fns';

type FormatFunctions = 'dateToMonthDay' | 'dateToPeriod' | 'dateToFull';

type GetArrayIntoStringProps<CustomType> = {
  array: CustomType[];
  propName: keyof CustomType;
  withSpace?: boolean;
};

const formatDate = (date: Date, options?: Intl.DateTimeFormatOptions, locale = 'en-US'): string => {
  const formatted = date.toLocaleDateString(locale, options);
  return formatted;
};

export const dateToMonthDayFormat = (
  date?: Date | string,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return '';

  const dateToUse = typeof date === 'string' ? new Date(date) : date;

  const localeOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    ...options,
  };

  return formatDate(dateToUse, localeOptions);
};

export const dateToPeriodFormat = (date: Date | string): string => {
  const dateToUse = typeof date === 'string' ? new Date(date) : date;

  const localeOptions: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
  };

  return formatDate(dateToUse, localeOptions);
};

export const dateToFullFormat = (date: Date | string): string => {
  const dateToUse = typeof date === 'string' ? new Date(date) : date;

  const localeOptions: Intl.DateTimeFormatOptions = {
    dateStyle: 'full',
  };

  return formatDate(dateToUse, localeOptions);
};

export const dateToMonthDayHourMeridiem = (date: Date | string): string => {
  const dateToUse = typeof date === 'string' ? new Date(date) : date;
  return format(dateToUse, 'MMM d HH:MM  a');
};

const formatFunctionsObject = {
  dateToMonthDay: dateToMonthDayFormat,
  dateToPeriod: dateToPeriodFormat,
  dateToFull: dateToFullFormat,
};

export const weekPeriodByDate = (
  date: Date | string,
  dateFormat: FormatFunctions = 'dateToMonthDay'
): [string, string] => {
  let dateToUse = typeof date === 'string' ? new Date(date) : date;

  if (isSaturday(dateToUse) || isSunday(dateToUse)) {
    dateToUse = nextMonday(dateToUse);
  }

  const weekStartDate = startOfWeek(dateToUse, { weekStartsOn: 1 });
  const weekEndDate = endOfWeek(dateToUse, { weekStartsOn: 1 });

  const weekEndingOnFridayDate = sub(weekEndDate, { days: 2 });

  const formatedStartDate = formatFunctionsObject[dateFormat](weekStartDate);
  const formatedEndDate = formatFunctionsObject[dateFormat](weekEndingOnFridayDate);

  return [formatedStartDate, formatedEndDate];
};

export const numberToPercent = (number: number, showNegativeSignal = true): string => {
  if (Number.isNaN(number)) {
    return '0%';
  }
  const centNumberWithTwoDecimals = (number * 100).toFixed(2);
  let formattedNumber = `${centNumberWithTwoDecimals}%`;

  if (formattedNumber.includes('.00')) {
    formattedNumber = formattedNumber.replace('.00', '');
  }

  if (number < 0 && !showNegativeSignal) {
    formattedNumber = formattedNumber.replace('-', '');
  }

  return formattedNumber;
};

export const numberToPercentNumber = (number: number): number => {
  return number * 100;
};

export const getFirstLetter = (name?: string): string => {
  if (!name) return '';
  return name.charAt(0);
};

export const getArrayIntoStringByProp = <CustomType>({
  array,
  propName,
  withSpace = true,
}: GetArrayIntoStringProps<CustomType>): string => {
  const arrayFormattedByPropName = array.map((value) => value[propName]);
  const arrayJoined = arrayFormattedByPropName.join(withSpace ? ', ' : ',');

  return arrayJoined;
};

export const getFormattedStatus = (status): string => {
  const spacesRegexPattern = /(^\w{1})|(\s+\w{1})/g;
  const statusWithoutUnderscore = (status ?? '').replace(/_/g, ' ');
  const statusLowercase = statusWithoutUnderscore.toLowerCase();
  const statusCapitalized = statusLowercase.replace(spacesRegexPattern, (letter) =>
    letter.toUpperCase()
  );

  return statusCapitalized;
};

export const DEFAULT_STATUS_COLOR = '#202123';

export const getStatusColor = (currentStatus, candidateStatuses): string => {
  const uppercaseStatus = currentStatus.toUpperCase();
  const findStatusGroup = ({ options }: any): boolean => options.includes(uppercaseStatus);
  const candidateStatusGroup = candidateStatuses.find(findStatusGroup);
  const statusColor = candidateStatusGroup?.color ?? DEFAULT_STATUS_COLOR;

  return statusColor;
};

export const getFirstAndLastNamesSplitted = (name?: string): string[] => {
  const nameSplitted = name?.split(' ') ?? [];
  const firstName = nameSplitted[0] ?? '';
  const lastName = nameSplitted.length > 1 ? (nameSplitted.at(-1) as string) : '';

  return [firstName, lastName];
};

export const getVisibleRichText = (value?: string): string =>
  (value || '').replace(/\s*<p>(\s*|(<br\s*\/?>))*<\/p>\s*/, '');

export const toOrdinal = (value: number): string => {
  const unit = value % 10;

  switch (unit) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};
